// =============================================================================
// CONSOLIDATED UI COMPONENTS
// =============================================================================
// This file consolidates 9 UI component files into a single module for better
// maintainability and reduced file fragmentation.
//
// Original Files:
// 1. card-wrappers.js (320 lines)
// 2. cardRegistry.js (284 lines)
// 3. simple-cards.js (228 lines)
// 4. WelcomeCards.js (190 lines)
// 5. ChatHeaderBar.js (156 lines)
// 6. LoopTimelineInline.js (136 lines)
// 7. RateLimitCard.js (110 lines)
// 8. cards/SlideActivityCard.js (42 lines)
// 9. cards/sharedCardStyles.js (21 lines)
// =============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  Collapse,
  Button,
  LinearProgress,
  CircularProgress,
  IconButton,
  Dialog,
  TextField
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { useSubscription } from '../../../settings/useSettingsScreen';
import {
  ExpandMoreIcon,
  RefreshIcon,
  CloudUploadIcon,
  AddIcon,
  InfoOutlinedIcon,
  DescriptionIcon,
  GridViewIcon,
  LogoutIcon,
  DarkModeIcon,
  LightModeIcon,
  MenuIcon,
  LayersIcon,
  CloseIcon,
  CheckIcon,
  FeedbackIcon,
  PushPinIcon,
  PushPinOutlinedIcon,
  ArchiveIcon
} from '../../../../shared/icons';
import { useAuth, useUser } from '../../../../shared/contexts/UserContext';
import { usePodMembership, useOrgAccess } from '../../../../shared/contexts';
import { buildFunctionUrl } from '../../../../shared/utils';
import { WorkflowsPanel } from './SidebarPanels';
import ConnectorStatusIndicator from './ConnectorStatusIndicator';
import {
  UnifiedCardContainer,
  UnifiedCardHeader,
  UnifiedCardTitle,
  UnifiedCardContent,
  ChatHeader
} from '../AIChat.styles';
import {
  ChatHistorySidebar,
  ChatHistorySidebarBackdrop,
  ChatHistoryItem,
  getChatRadii,
  getChatBorders
} from '../chatStyles';
import AIInputSurface from '../AIInputSurface';
import EnhancedMarkdownRenderer from './EnhancedMarkdownRenderer';
import MessageRenderer from './MessageRenderer';
import { SlideBuilderCard, ResearchCard } from './AiChatCards';
import { formatTime, formatChatTimestampShort, getRestackTokens } from '../utils';

// =============================================================================
// SECTION 1: CARD WRAPPERS
// Original Files: ChatMessageCard.js, UnifiedCard.js, componentRenderers.js
// =============================================================================

const MessageWrapper = styled(Box, {
  shouldForwardProp: (prop) => !['sender', 'isWelcome'].includes(prop)
})(({ theme, sender, isWelcome }) => {
  const { tokens } = theme.restack;
  const isUser = sender === 'user';
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(tokens.spacing.xs),
    paddingTop: isWelcome ? 0 : theme.spacing(isUser ? tokens.spacing.sm : tokens.spacing.xs),
    paddingRight: isWelcome ? 0 : theme.spacing(tokens.spacing.md),
    paddingBottom: 0,
    paddingLeft: isWelcome ? 0 : (isUser ? theme.spacing(tokens.spacing.md) : 0),
    alignItems: isWelcome ? 'center' : (isUser ? 'flex-end' : 'flex-start'),
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    '&:first-of-type': {
      paddingTop: isWelcome ? 0 : theme.spacing(tokens.spacing.xs)
    }
  };
});

const BodyContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isWelcome'
})(({ theme, isWelcome }) => {
  const { tokens } = theme.restack;
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(tokens.spacing.nano),
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,  // Allow shrinking
    boxSizing: 'border-box',
    alignItems: isWelcome ? 'center' : 'stretch'
  };
});

const ActionRow = styled(Box)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(tokens.spacing.nano)
  };
});

const ActionChip = styled(ButtonBase)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    ...tokens.typography.bodySm,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(tokens.spacing.nano),
    padding: theme.spacing(tokens.spacing.quark * 2, tokens.spacing.sm),
    borderRadius: getChatRadii(theme).chip,
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    border: getChatBorders(theme).resting,
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary
    }
  };
});

export const ChatMessageCard = ({
  message,
  isStreaming = false,
  actionChips = [],
  onActionClick,
  children,
  isWelcome = false
}) => {
  const timestampLabel = useMemo(() => {
    if (!message?.timestamp) {
      return null;
    }
    return formatTime(message.timestamp);
  }, [message?.timestamp]);

  return (
    <MessageWrapper
      data-message-id={message?.id}
      data-streaming={isStreaming}
      sender={message?.sender}
      isWelcome={isWelcome}
    >
      <BodyContainer isWelcome={isWelcome}>
        {children || <MessageRenderer message={message} />}
      </BodyContainer>
      {timestampLabel && message?.sender !== 'ai' && (
        <Typography
          component="time"
          dateTime={message.timestamp}
          sx={theme => ({
            ...theme.restack.tokens.typography.labelXs,
            color: theme.palette.text.secondary,
            opacity: 0.7,
            alignSelf: message?.sender === 'user' ? 'flex-end' : 'flex-start',
            textAlign: message?.sender === 'user' ? 'right' : 'left',
            fontWeight: theme.typography.fontWeightRegular
          })}
        >
          {timestampLabel}
        </Typography>
      )}

      {actionChips.length > 0 && (
        <ActionRow>
          {actionChips.map((chip) => (
            <ActionChip
              key={chip.id || chip.label}
              onClick={() => onActionClick?.(chip)}
            >
              {chip.icon}
              {chip.label}
            </ActionChip>
          ))}
        </ActionRow>
      )}
    </MessageWrapper>
  );
};

export const UnifiedCard = ({
  icon,
  title,
  subtitle,
  children,
  isCollapsible = false,
  defaultExpanded = false,
  onClick,
  noBorder = false,
  isLoading = false,
  noPadding = false,
  forceExpanded = null,
  centerTitle = false,
  actionButton = null
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  React.useEffect(() => {
    if (forceExpanded !== null) {
      setExpanded(forceExpanded);
    } else {
      setExpanded(defaultExpanded);
    }
  }, [defaultExpanded, forceExpanded]);

  const handleToggle = () => {
    if (isCollapsible) {
      setExpanded(!expanded);
    }
    if (onClick) {
      onClick();
    }
  };

  const renderIcon = () => {
    if (!icon) {
      return null;
    }

    if (React.isValidElement(icon)) {
      const existingSx = icon.props?.sx || {};
      const mergedSx = {
        fontSize: theme.typography.pxToRem(16),
        color: 'inherit',
        ...existingSx
      };

      return React.cloneElement(icon, { sx: mergedSx });
    }

    return icon;
  };

  return (
    <UnifiedCardContainer sx={(theme) => ({
      border: noBorder ? 'none' : undefined,
      marginBottom: noBorder ? 0 : theme.spacing(theme.restack.tokens.spacing.xs),
      padding: 0,
      backgroundColor: noBorder ? 'transparent' : undefined
    })}>
      <UnifiedCardHeader onClick={handleToggle} centerTitle={centerTitle}>
        {/* Left side: Icon + Title + Chevron grouped */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(theme.restack.tokens.spacing.xs),
          flex: 1,
          minWidth: 0
        }}>
          {icon ? (
            <Box sx={{
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: theme.spacing(3),
              height: theme.spacing(3),
              borderRadius: getChatRadii(theme).chip,
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
              flexShrink: 0
            }}>
              {renderIcon()}
            </Box>
          ) : null}
          <UnifiedCardTitle sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title}
            </Box>
            {subtitle && (
              <Box
                component="span"
                sx={(theme) => ({
                  ...theme.restack.tokens.typography.labelXs,
                  color: theme.palette.text.secondary,
                  fontWeight: theme.typography.fontWeightRegular,
                  marginLeft: theme.spacing(theme.restack.tokens.spacing.nano),
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                })}
              >
                {subtitle}
              </Box>
            )}
          </UnifiedCardTitle>
          {isCollapsible && (
            <ExpandMoreIcon
              sx={(theme) => ({
                fontSize: theme.spacing(2.25),
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                flexShrink: 0
              })}
            />
          )}
        </Box>

        {/* Right side: Action button */}
        {actionButton && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
            {actionButton}
          </Box>
        )}
      </UnifiedCardHeader>
      {isCollapsible ? (
        <Collapse in={expanded}>
          <UnifiedCardContent
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: theme.spacing(5),
              padding: noPadding ? 0 : undefined
            })}
          >
            {children}
          </UnifiedCardContent>
        </Collapse>
      ) : (
        <UnifiedCardContent sx={{ padding: noBorder || noPadding ? 0 : undefined }}>
          {children}
        </UnifiedCardContent>
      )}
    </UnifiedCardContainer>
  );
};

// =============================================================================
// SECTION 2: CARD REGISTRY
// Original File: cardRegistry.js
// =============================================================================

const buildSlideCard = (variant) => {
  const timelineType = variant === 'creation' ? 'createSlides' : 'editSlides';

  return {
    label: null,
    timelineType,
    render: ({ component, key }, context = {}) => ({
      element: (
        <SlideActivityCard
          key={key}
          variant={variant}
          data={component.data}
          currentSlideCount={context.currentSlideCount}
          isMessageStreaming={context.isMessageStreaming}
        />
      ),
      timelineType
    }),
    buildTimelineEntry: () => ({
      label: null,
      detail: null,
      showHeader: false
    })
  };
};

const mapResearchDataToProps = (data = {}) => {
  console.log('🔍 mapResearchDataToProps INPUT:', {
    hasData: !!data,
    dataKeys: Object.keys(data || {}),
    hasSearchQueries: Array.isArray(data.searchQueries),
    searchQueriesCount: data.searchQueries?.length || 0,
    searchQueriesPreview: data.searchQueries?.slice(0, 2)
  });

  const mapped = {
    query: data.query,
    keyFindings: Array.isArray(data.keyFindings) ? data.keyFindings : [],
    fullReport: data.fullReport ?? data.report ?? data.content ?? null,
    report: data.report ?? data.content ?? null,
    citations: Array.isArray(data.citations) && data.citations.length > 0
      ? data.citations
      : Array.isArray(data.sources) ? data.sources : [],
    sourceSummaries: Array.isArray(data.sourceSummaries) ? data.sourceSummaries : [],
    searchQueries: Array.isArray(data.searchQueries) ? data.searchQueries : [],
    fetchedPages: Array.isArray(data.fetchedPages) ? data.fetchedPages : [],
    reportDuration: data.reportDuration ?? data.duration ?? null,
    citationsDuration: data.citationsDuration ?? null,
    isLoading: data.isLoading ?? false,
    status: data.status ?? null
  };

  console.log('🔍 mapResearchDataToProps OUTPUT:', {
    hasSearchQueries: Array.isArray(mapped.searchQueries),
    searchQueriesCount: mapped.searchQueries.length,
    mappedKeys: Object.keys(mapped)
  });

  return mapped;
};

const CARD_REGISTRY = {
  createSlides: buildSlideCard('creation'),
  editSlides: buildSlideCard('edit'),
  research: {
    label: 'Research',
    timelineType: 'research',
    render: ({ component, key }) => {
      const data = component.data || {};

      // DIAGNOSTIC: Check raw component data before mapping
      console.log('🔍 RAW COMPONENT DATA BEFORE MAPPING:', {
        componentKeys: Object.keys(component),
        dataKeys: Object.keys(data),
        hasSearchQueriesInData: 'searchQueries' in data,
        searchQueriesValue: data.searchQueries,
        fullComponentDump: JSON.stringify(component, null, 2)
      });

      const mappedProps = mapResearchDataToProps(data);

      console.log('🔍 RENDERING RESEARCH CARD:', {
        componentType: component.type,
        hasData: !!data,
        keyFindingsCount: mappedProps.keyFindings?.length || 0,
        fullReportLength: mappedProps.fullReport?.length || 0,
        reportLength: mappedProps.report?.length || 0,
        citationsCount: mappedProps.citations?.length || 0,
        isLoading: mappedProps.isLoading,
        status: mappedProps.status,
        rawDataKeys: Object.keys(data),
        rawKeyFindings: data.keyFindings,
        rawFullReport: data.fullReport ? `${data.fullReport.substring(0, 50)}...` : null
      });

      return {
        element: (
          <ResearchCard
            key={key}
            {...mappedProps}
          />
        ),
        timelineType: 'research'
      };
    },
    buildTimelineEntry: ({ component }) => {
      const data = component.data || {};
      const citationCount = data.citations?.length || 0;
      const label = data.isLoading ? 'Researching' : 'Research';
      const detail = citationCount > 0 ? `${citationCount} sources` : null;
      return { label, detail, showHeader: false };
    }
  },
  rateLimit: {
    label: 'Usage limit',
    timelineType: 'rateLimit',
    render: ({ component, key }) => ({
      element: (
        <RateLimitCard
          key={key}
          adminFirstName={component.data?.adminFirstName}
        />
      ),
      timelineType: 'rateLimit'
    }),
    buildTimelineEntry: () => null  // Suppress header - card is self-contained
  },
  turnError: {
    label: 'Turn stopped',
    timelineType: 'turnError',
    render: ({ component, key }) => ({
      element: (
        <TurnErrorCard
          key={key}
          message={component.data?.message}
        />
      ),
      timelineType: 'turnError'
    }),
    buildTimelineEntry: () => null  // Suppress header - card is self-contained
  },
  retry: {
    label: 'Retry available',
    timelineType: 'retry',
    render: ({ component, key }, context = {}) => ({
      element: (
        <RetryButton
          key={key}
          originalMessage={component.originalMessage}
          onRetry={context.onRetryMessage}
        />
      ),
      timelineType: 'retry'
    }),
    buildTimelineEntry: () => ({
      label: 'Retry available',
      detail: 'send again'
    })
  },
  welcome_cards: {
    label: null,
    timelineType: 'welcome_cards',
    render: ({ component, key }, context) => ({
      element: (
        <WelcomeCards
          key={key}
          data={component.data}
          onSuggestionClick={context?.onSuggestionClick}
        />
      ),
      timelineType: 'welcome_cards'
    }),
    buildTimelineEntry: () => ({
      label: null,
      detail: null,
      showHeader: false
    })
  },
  loopTimeline: {
    label: null,
    timelineType: 'loopTimeline',
    render: ({ component, key }) => ({
      element: (
        <LoopTimelineInline
          key={key}
          data={component.data}
        />
      ),
      timelineType: 'loopTimeline'
    }),
    buildTimelineEntry: ({ component }) => {
      const data = component?.data || {};
      const steps = Array.isArray(data.steps) ? data.steps : [];
      const summary = data.summary || {};

      const activeStep = steps.find(step => step.status === 'in_progress');
      const completedCount = summary.completedCount || steps.filter(s => s.status === 'completed').length;

      const label = activeStep
        ? activeStep.intentLabel || activeStep.primaryTool || 'Processing'
        : completedCount > 0
          ? `${completedCount} ${completedCount === 1 ? 'action' : 'actions'} completed`
          : 'Activity';

      return {
        label,
        detail: null,
        showHeader: false
      };
    }
  },
  slideWork: {
    label: null,
    timelineType: 'slideWork',
    render: ({ key }) => ({
      element: <React.Fragment key={key} />,
      timelineType: 'slideWork'
    }),
    buildTimelineEntry: ({ component }) => {
      const data = component.data || {};
      const indices = Array.isArray(data.touchedSlideIndices) ? data.touchedSlideIndices : [];
      const isComplete = data.isComplete === true;
      const verb = isComplete ? 'Modified' : 'Working on';
      let label;
      // A WORKBOOK HAS NO SLIDES AND A DOCUMENT HAS NO SLIDES.
      //
      // Slide indices are a PowerPoint concept: on the other two hosts the
      // array is always empty, so this card fell through to the literal
      // "Working on slide" and told an Excel user their workbook was a deck.
      // Neither host has an equivalent identity to count (an Excel write
      // carries an opaque sheet tabId, not a name), so the honest detail is
      // how much was done. `app` is stamped on the card by the reducer; a card
      // without one is PowerPoint's, and takes the original path unchanged.
      if (data.app === 'excel' || data.app === 'word') {
        const noun = data.app === 'excel' ? 'the workbook' : 'the document';
        const operations = Number.isFinite(data.operationCount) ? data.operationCount : 0;
        label = operations > 1
          ? `${verb} ${noun} (${operations} edits)`
          : `${verb} ${noun}`;
      } else if (indices.length === 0) {
        // Pure execute_office_js writes — slide index unknowable.
        label = `${verb} slide`;
      } else if (indices.length === 1) {
        label = `${verb} slide ${indices[0] + 1}`;
      } else if (indices.length === 2) {
        label = `${verb} slides ${indices[0] + 1}, ${indices[1] + 1}`;
      } else {
        label = `${verb} ${indices.length} slides`;
      }
      return {
        label,
        detail: null,
        showHeader: true,
        hideMarker: false,
        isActive: !isComplete,
        isComplete
      };
    }
  },
  toolStatus: {
    label: null,
    timelineType: 'toolStatus',
    render: ({ component, key }) => ({
      element: <React.Fragment key={key} />,
      timelineType: 'toolStatus'
    }),
    buildTimelineEntry: ({ component }) => {
      const data = component.data || {};
      const isComplete = data.status === 'completed' || data.status === 'failed';
      const baseLabel = data.label || 'Tool';
      const count = Number.isFinite(data.count) ? data.count : 1;
      // Always show the total invocation count as `×N` — the in-progress
      // `(0/N)` format was visually noisy when the same tool fired many
      // times in a row (e.g., `Reading Text (0/17)`). A single `×17`
      // communicates the same intent with much less ink.
      const label = count > 1 ? `${baseLabel} ×${count}` : baseLabel;
      return {
        label,
        detail: null,
        showHeader: true,
        hideMarker: false,
        isActive: !isComplete,
        isComplete
      };
    }
  },
  subagent: {
    label: null,
    timelineType: 'subagent',
    render: ({ component, key }) => {
      return {
        element: null,
        timelineType: 'subagent'
      };
    },
    buildTimelineEntry: () => {
      return {
        label: null,
        detail: null,
        showHeader: false
      };
    }
  }
};

export const getCardDescriptor = (type) => CARD_REGISTRY[type] || null;

export const getTimelineLabel = (type) => {
  const descriptor = getCardDescriptor(type);
  return descriptor?.label || null;
};

export const createComponentRenderers = (context = {}) => {
  const renderers = {};

  Object.entries(CARD_REGISTRY).forEach(([type, descriptor]) => {
    if (typeof descriptor?.render !== 'function') {
      return;
    }

    renderers[type] = ({ component, key, index }) => descriptor.render({ component, key, index }, context);
  });

  return renderers;
};

export const createTimelineResolvers = (context = {}) => {
  const resolvers = {};

  Object.entries(CARD_REGISTRY).forEach(([type, descriptor]) => {
    if (typeof descriptor?.buildTimelineEntry !== 'function') {
      return;
    }

    resolvers[type] = ({ component, index }) => descriptor.buildTimelineEntry({ component, index }, context);
  });

  return resolvers;
};

// =============================================================================
// SECTION 3: SIMPLE CARDS
// Original Files: ChatComposer.js, RetryButton.js, ThinkingCard.js, UploadStatusCard.js
// =============================================================================

export const ChatComposer = (
  {
    composer,
    isLoading,
    onSend,
    placeholder,
    onStopRequest,
    readOnly = false,
    renderTextareaOverlay,
    minCharacters = 1,
    selectedModel,
    onModelChange,
    awaitingPeerLabel = null,
    slashCommands = [],
    onCompactChat = null,
    onCancelPeerWait = null,
    liveDataAvailable = false,
    liveDataMode = false,
    onLiveDataModeChange = null,
    privateSessionActive = false,
  },
) => {
  return (
    <AIInputSurface
      composer={composer}
      isLoading={isLoading}
      onSend={onSend}
      placeholder={placeholder}
      onStopRequest={onStopRequest}
      minCharacters={minCharacters}
      allowAttachments
      allowModeSelection
      allowModelSelection={true}
      selectedModel={selectedModel}
      onModelChange={onModelChange}
      forceDisableSend={readOnly}
      renderTextareaOverlay={renderTextareaOverlay}
      awaitingPeerLabel={awaitingPeerLabel}
      slashCommands={slashCommands}
      onCompactChat={onCompactChat}
      onCancelPeerWait={onCancelPeerWait}
      liveDataAvailable={liveDataAvailable}
      liveDataMode={liveDataMode}
      onLiveDataModeChange={onLiveDataModeChange}
      privateSessionActive={privateSessionActive}
    />
  );
};

export const RetryButton = ({ originalMessage, onRetry }) => {
  const theme = useTheme();
  const { tokens } = theme.restack;

  const handleRetry = () => {
    if (onRetry && originalMessage) {
      onRetry(originalMessage);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      marginTop: theme.spacing(tokens.spacing.sm),
    }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<RefreshIcon />}
        onClick={handleRetry}
        sx={{
          fontSize: theme.typography.pxToRem(12),
          padding: theme.spacing(tokens.spacing.nano, tokens.spacing.sm),
          textTransform: 'none',
        }}
      >
        Try again
      </Button>
    </Box>
  );
};

export const ThinkingCard = ({
  content,
  isStreaming,
  compact = false,
  title = 'Thinking',
  durationLabel = null,
  durationSeconds = 0,
  startTime = null,
  isActive = false,
  showInterruptHint = false,
  isCollapsed = true,
  onToggleCollapse
}) => {
  const theme = useTheme();
  const tokens = getRestackTokens(theme);

  const containerStyles = {
    maxWidth: 'min(680px, 100%)',
    marginTop: 0,
    marginBottom: 0
  };

  return (
    <Box sx={containerStyles}>
      <Collapse in={!isCollapsed} appear={false} unmountOnExit={false}>
        <Box
          sx={{
            paddingLeft: 0,
            paddingTop: theme.spacing(tokens.spacing.nano),
            '& *': {
              color: alpha(theme.palette.text.primary, 0.7),
              fontStyle: 'italic'
            },
            '& strong': {
              fontWeight: theme.typography.fontWeightMedium
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              fontStyle: 'italic',
              color: alpha(theme.palette.text.primary, 0.75)
            }
          }}
        >
          <EnhancedMarkdownRenderer content={content} theme={theme} />
        </Box>
      </Collapse>
    </Box>
  );
};

export const UploadStatusCard = ({ files, message, isComplete = false }) => {
  const theme = useTheme();
  const { tokens } = theme.restack;

  return (
    <UnifiedCardContainer>
      <UnifiedCardHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(tokens.spacing.xs) }}>
          <CloudUploadIcon sx={{ fontSize: theme.spacing(tokens.spacing.md), color: 'primary.main' }} />
          <Typography
            variant="body2"
            sx={{ fontWeight: theme.typography.fontWeightRegular }}
          >
            {isComplete ? 'Files Uploaded' : 'Uploading Files'}
          </Typography>
        </Box>
      </UnifiedCardHeader>
      <UnifiedCardContent>
        <Typography variant="body2" sx={{ mb: theme.spacing(tokens.spacing.sm), color: 'text.secondary' }}>
          {message || 'Uploading files to secure storage...'}
        </Typography>

        {files && files.map((file, index) => (
          <Box key={index} sx={{ mb: theme.spacing(tokens.spacing.xs) }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {file.name}
            </Typography>
            {!isComplete && (
              <LinearProgress
                sx={{
                  mt: theme.spacing(tokens.spacing.nano),
                  height: theme.spacing(0.25),
                  backgroundColor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'primary.main'
                  }
                }}
              />
            )}
          </Box>
        ))}
      </UnifiedCardContent>
    </UnifiedCardContainer>
  );
};

// =============================================================================
// SECTION 4: WELCOME CARDS
// Original File: WelcomeCards.js
// =============================================================================

const WelcomeCards = ({ data, onSuggestionClick }) => {
  const theme = useTheme();
  const { tokens } = theme.restack;
  const chatRadii = getChatRadii(theme);
  const chatBorders = getChatBorders(theme);
  const { cards = [], prompt, heading, description, app = 'powerpoint' } = data || {};
  const resolvedCards = Array.isArray(cards) ? cards.slice(0, 4) : [];
  const isDark = theme.palette.mode === 'dark';
  const WelcomeIcon = app === 'word'
    ? DescriptionIcon
    : app === 'excel'
      ? GridViewIcon
      : LayersIcon;

  const containerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1.5, tokens.spacing.sm),
    width: '100%',
    maxWidth: theme.spacing(60),
    margin: '0 auto',
    minHeight: '100%',
    boxSizing: 'border-box'
  };

  const heroStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    textAlign: 'center'
  };

  const cardGridStyles = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.875),
    width: '100%',
    maxWidth: theme.spacing(48),
    marginBottom: prompt ? theme.spacing(tokens.spacing.md) : 0
  };

  const cardStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'auto',
    maxWidth: '100%',
    appearance: 'none',
    fontFamily: 'inherit',
    padding: theme.spacing(0.85, 1.25),
    borderRadius: chatRadii.pill,
    backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.035 : 0.025),
    border: chatBorders.resting,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.06 : 0.045),
      borderColor: chatBorders.emphasisColor,
    },
    '&:focus-visible': {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
      outlineOffset: 2,
    }
  };

  const promptStyles = {
    textAlign: 'center',
    padding: theme.spacing(tokens.spacing.sm + tokens.spacing.nano),
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    borderRadius: chatRadii.card,
    border: `${chatBorders.width}px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    maxWidth: theme.spacing(50)
  };

  return (
    <Box sx={containerStyles}>
      {/* Hero Section */}
      <Box sx={heroStyles}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: chatRadii.nested,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.25,
            backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.06 : 0.04),
          }}
        >
          <WelcomeIcon
            sx={{
              fontSize: 20,
              color: alpha(theme.palette.text.primary, isDark ? 0.45 : 0.35),
            }}
          />
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: theme.typography.pxToRem(16),
            color: theme.palette.text.primary,
            letterSpacing: '-0.015em',
            lineHeight: 1.3
          }}
        >
          {heading || 'What do you want to do?'}
        </Typography>
        {description && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              maxWidth: theme.spacing(38),
              fontSize: theme.typography.pxToRem(12),
              lineHeight: 1.5,
              color: 'text.secondary',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {/* Suggestion Cards */}
      <Box sx={cardGridStyles}>
        {resolvedCards.map((card) => (
          <Box
            key={card.id}
            component="button"
            type="button"
            sx={cardStyles}
            onClick={() => onSuggestionClick?.(card.prompt || card.title)}
            aria-label={`Use ${card.title} workflow`}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: theme.typography.fontWeightRegular,
                fontSize: theme.typography.pxToRem(12.25),
                color: alpha(theme.palette.text.primary, isDark ? 0.7 : 0.55),
                lineHeight: 1.4,
                textAlign: 'center',
                whiteSpace: 'normal',
              }}
            >
              {card.title}
            </Typography>
          </Box>
        ))}
      </Box>

      {prompt && (
        <Box sx={promptStyles}>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: theme.typography.fontWeightRegular,
              fontSize: theme.typography.pxToRem(14)
            }}
          >
            {prompt}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// SECTION 5: CHAT HEADER BAR
// Original File: ChatHeaderBar.js
// =============================================================================

// localStorage key prefix for per-pod credit high-water-mark tracking.
// Used as the progress-bar denominator for prepaid-only users (free plan +
// SANDBOX / booster) where there's no monthly allowance to chart against.
// As the user's balance climbs from a new grant, the high-water rises with
// it; as they spend, the bar visibly drains.
const CREDITS_HIGH_WATER_KEY_PREFIX = 'restack_credits_hw';

/**
 * CreditBalanceBar — credit balance + progress visualization for the
 * history sidebar footer (only — no longer rendered above the composer).
 *
 * Always shows a LinearProgress bar matching the Settings page style:
 *   height 10, radius 10, primary alpha 0.1 track.
 *   Tier colors: ≤20% error, ≤50% warning, else primary.
 *
 * Denominator strategy:
 *   • If monthly allowance > 0 (paid plan): bar = subscriptionCredits /
 *     monthlyCredits. Add-on credits shown as a "+N add-on" tail.
 *   • Else (prepaid-only — free plan + SANDBOX / booster): bar =
 *     creditBalance / pod high-water-mark stored in localStorage. The HW
 *     ratchets up whenever the balance exceeds the stored value, so a
 *     fresh grant lands at 100% and progressively drains.
 */
export const CreditBalanceBar = ({ variant = 'sidebar' }) => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const orgAccess = useOrgAccess();
  const { isLoading: isPodLoading, pod } = usePodMembership();
  const {
    plan,
    creditBalance,
    subscriptionCredits,
    prepaidCredits,
    monthlyCredits,
    currentPeriodEnd,
    cycleEndsAt,
    planConfig
  } = useSubscription();

  const podId = pod?.id || pod?.podId || null;
  const hwKey = podId ? `${CREDITS_HIGH_WATER_KEY_PREFIX}_${podId}` : null;

  const totalBalance = Math.max(0, Number(creditBalance) || 0);

  // Ratchet up the stored high-water mark whenever the live balance
  // exceeds it. The denominator is then read off the same value during
  // render so the bar fills smoothly toward 100% on grant.
  useEffect(() => {
    if (!hwKey || typeof window === 'undefined') return;
    try {
      const stored = parseInt(window.localStorage.getItem(hwKey) || '0', 10) || 0;
      if (totalBalance > stored) {
        window.localStorage.setItem(hwKey, String(Math.ceil(totalBalance)));
      }
    } catch { /* ignore */ }
  }, [hwKey, totalBalance]);

  if (!isAuthenticated || isPodLoading) return null;
  if (totalBalance === 0) return null;
  // Org members get exactly ONE meter: MemberCreditsBar ("Resets" = the
  // monthly credit refresh boundary). Mounting this pod bar next to it
  // showed a SECOND date with different semantics ("Renews" = the Stripe
  // billing date), which read as a bug. This bar is for non-org pod users.
  if (orgAccess?.orgId) return null;

  const subCredits = Math.max(0, Number(subscriptionCredits) || 0);
  const addOn = Math.max(0, Number(prepaidCredits) || 0);
  const monthly = Math.max(0, Number(monthlyCredits) || 0);
  const hasMonthlyAllowance = monthly > 0;

  // Only subscription credits renew. Prepaid (SANDBOX / booster / DEMO)
  // credits are one-shot lots with per-lot expiry, not monthly renewal.
  // So suppress the "Renews X" label entirely when there's no monthly
  // allowance — showing it for prepaid-only users is misleading.
  const hasMonthly = Number(monthlyCredits) > 0;
  const renewsAt = hasMonthly ? (currentPeriodEnd || cycleEndsAt) : null;
  const renewsLabel = renewsAt
    ? new Date(renewsAt).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' })
    : null;
  const planLabel = planConfig?.name || (plan === 'free' ? 'Free' : null);

  // Compute denominator + percentage.
  let reference;
  let progressNumerator;
  if (hasMonthlyAllowance) {
    reference = Math.max(monthly, 1);
    progressNumerator = subCredits;
  } else {
    // Prepaid-only: read high-water from localStorage, fall back to
    // current balance (renders the bar at 100% if no prior history).
    let storedHw = 0;
    if (hwKey && typeof window !== 'undefined') {
      try { storedHw = parseInt(window.localStorage.getItem(hwKey) || '0', 10) || 0; } catch { /* ignore */ }
    }
    reference = Math.max(storedHw, totalBalance, 1);
    progressNumerator = totalBalance;
  }
  const pct = Math.min(100, (progressNumerator / reference) * 100);

  // Tier colors mirror Settings page exactly.
  let barColor = theme.palette.primary.main;
  if (pct <= 20) barColor = theme.palette.error.main;
  else if (pct <= 50) barColor = theme.palette.warning.main;

  const wrapperSx = {
    px: 2,
    pt: 1.25,
    pb: 1.25,
  };

  return (
    <Box sx={wrapperSx}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75, gap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: theme.typography.pxToRem(11),
            color: 'text.primary',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {Math.floor(totalBalance).toLocaleString()}
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.5 }}>
            {hasMonthlyAllowance
              ? `/ ${reference.toLocaleString()} credits`
              : (totalBalance === 1 ? 'credit' : 'credits')}
          </Box>
          {hasMonthlyAllowance && addOn > 0 && (
            <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 500, opacity: 0.8 }}>
              +{Math.floor(addOn).toLocaleString()} add-on
            </Box>
          )}
        </Typography>
        {(renewsLabel || planLabel) && (
          <Typography
            variant="caption"
            sx={{
              fontSize: theme.typography.pxToRem(10),
              color: 'text.secondary',
              opacity: 0.75,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            {renewsLabel ? `Renews ${renewsLabel}` : planLabel}
          </Typography>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 10,
          borderRadius: getChatRadii(theme).pill,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          '& .MuiLinearProgress-bar': {
            backgroundColor: barColor,
            borderRadius: getChatRadii(theme).pill,
            transition: 'transform 0.6s ease, background-color 0.4s ease',
          },
        }}
      />
    </Box>
  );
};

/**
 * MemberCreditsBar - an ORG MEMBER's own usage meter for the sidebar footer.
 *
 * Shows the signed-in member's personal consumption this cycle against the
 * per-seat allowance (e.g. 800), draining left to right like the pod bar
 * above. When the seat allowance is exhausted the member is drawing from the
 * shared firm pool: the bar flips to a full warning-tinted state with a
 * "Drawing from firm pool" label (+N overage), and to an error state when
 * the pool itself is empty. Deliberate scope: the member sees only THEIR
 * numbers; the firm pool remains numberless (state only, admin console has
 * the figures).
 */
export const MemberCreditsBar = () => {
  const theme = useTheme();
  const { isAuthenticated, currentUser } = useAuth();
  const orgAccess = useOrgAccess();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !currentUser || !orgAccess?.orgId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const idToken = await currentUser.getIdToken();
        const res = await fetch(buildFunctionUrl('getMyCreditUsage', { forceProduction: true }), {
          method: 'GET',
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        if (!cancelled && data?.org) setUsage(data);
      } catch { /* meter is best-effort chrome */ }
    };
    load();
    const interval = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAuthenticated, currentUser, orgAccess?.orgId]);

  if (!orgAccess?.orgId || !usage?.org) return null;

  const allowance = Math.max(1, Number(usage.seatAllowance) || 800);
  const used = Math.max(0, Number(usage.creditsUsedThisCycle) || 0);
  const remaining = Math.max(0, allowance - used);
  const onPool = Boolean(usage.drawingFromPool);
  const poolEmpty = usage.poolState === 'exhausted';
  const overage = Math.max(0, used - allowance);

  const pct = onPool ? 100 : Math.min(100, (remaining / allowance) * 100);
  let barColor = theme.palette.primary.main;
  if (poolEmpty) barColor = theme.palette.error.main;
  else if (onPool) barColor = alpha(theme.palette.warning.main, 0.75);
  else if (pct <= 20) barColor = theme.palette.error.main;
  else if (pct <= 50) barColor = theme.palette.warning.main;

  const resetsLabel = usage.cycleEndsAt
    ? new Date(usage.cycleEndsAt).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' })
    : null;

  let label;
  if (poolEmpty) {
    label = 'Firm pool empty';
  } else if (onPool) {
    label = 'Drawing from firm pool';
  } else {
    label = null;
  }

  return (
    <Box sx={{ px: 2, pt: 1.25, pb: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75, gap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: theme.typography.pxToRem(11),
            color: poolEmpty ? 'error.main' : 'text.primary',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label ? (
            <>
              {label}
              {onPool && !poolEmpty && overage > 0 && (
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.5 }}>
                  +{Math.floor(overage).toLocaleString()} this cycle
                </Box>
              )}
            </>
          ) : (
            <>
              {Math.floor(remaining).toLocaleString()}
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.5 }}>
                of {allowance.toLocaleString()} credits left
              </Box>
            </>
          )}
        </Typography>
        {resetsLabel && (
          <Typography
            variant="caption"
            sx={{
              fontSize: theme.typography.pxToRem(10),
              color: 'text.secondary',
              opacity: 0.75,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            Resets {resetsLabel}
          </Typography>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 10,
          borderRadius: getChatRadii(theme).pill,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          '& .MuiLinearProgress-bar': {
            backgroundColor: barColor,
            borderRadius: getChatRadii(theme).pill,
            transition: 'transform 0.6s ease, background-color 0.4s ease',
          },
        }}
      />
      {poolEmpty && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: theme.typography.pxToRem(10.5), color: 'text.secondary' }}>
          Ask your admin to top up.
        </Typography>
      )}
    </Box>
  );
};

// localStorage key for persistent dismissal of the sandbox banner. Keyed
// per-user so dismissing in one account doesn't suppress the prompt for
// another. Naturally moot once the user has any credits (the banner only
// renders for free-plan + zero balance), but persists across sessions for
// users who intentionally dismissed it.
const SANDBOX_BANNER_DISMISSED_KEY = 'restack_sandbox_banner_dismissed';

/**
 * SandboxRedemptionBanner — first-mile activation surface for associates who
 * land in the plugin without context. Used in two placements:
 *   1. Above the chat composer (variant="composer", default)
 *   2. Inside the history sidebar footer above account info (variant="sidebar")
 *
 * Styled as a floating card with margin from the surrounding edges (rounded
 * border, subtle tint) so it reads as an actionable prompt the user can
 * either complete or dismiss, similar to the AskUserQuestion modal pattern.
 *
 * Visibility: free-plan users with zero credits, unless dismissed via the
 * close X (persisted per-user in localStorage). Success state renders
 * briefly after redemption, then the banner naturally yields the slot to
 * CreditBalanceBar on next render.
 */
export const SandboxRedemptionBanner = ({ variant = 'composer' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isAuthenticated, currentUser } = useAuth();
  const { plan, creditBalance } = useSubscription();
  const { isLoading: isPodLoading } = usePodMembership();
  const orgAccess = useOrgAccess();

  const userId = currentUser?.uid || 'anon';
  const dismissalKey = `${SANDBOX_BANNER_DISMISSED_KEY}_${userId}`;

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [redeemedMessage, setRedeemedMessage] = useState(null);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.localStorage.getItem(dismissalKey) === '1'; }
    catch { return false; }
  });

  // Auto-dismiss the success state after 7s so the banner doesn't linger
  // forever once credits are confirmed. The CreditBalanceBar will pick up
  // the new balance display on next render. If the user manually dismisses
  // sooner via X, the unmount cleanup cancels the timer.
  // NOTE: this useEffect must run unconditionally on every render to
  // satisfy the Rules of Hooks — keep it above the early returns below.
  useEffect(() => {
    if (!redeemedMessage) return undefined;
    const timer = setTimeout(() => {
      setDismissed(true);
      try { window.localStorage.setItem(dismissalKey, '1'); } catch { /* ignore */ }
    }, 7000);
    return () => clearTimeout(timer);
  }, [redeemedMessage, dismissalKey]);

  if (!isAuthenticated || isPodLoading || orgAccess.isLoading) return null;
  // Org members draw from the shared org pool — the SANDBOX (free-trial) prompt is
  // only for users WITHOUT an org. A member's personal plan reads 'free' and the
  // pool balance is hidden by the adoption guard (reads 0), so without this guard
  // the banner would never go away after they claim a seat.
  if (orgAccess.orgId) return null;

  const shouldShow = !dismissed && (
    (plan === 'free' && (creditBalance || 0) === 0) || redeemedMessage
  );
  if (!shouldShow) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem(dismissalKey, '1'); } catch { /* ignore */ }
  };

  const handleActivate = async () => {
    const normalized = (code || '').trim().toUpperCase();
    if (!normalized) {
      setError('Enter the code from your demo email.');
      return;
    }
    if (loading || !currentUser) return;
    setLoading(true);
    setError(null);
    try {
      // forceProduction:true so the plugin works even when the dev frontend
      // is on https://localhost:3000 — without it, buildFunctionUrl resolves
      // to http://localhost:5001 (the Functions emulator), which (a) isn't
      // running, and (b) the browser blocks anyway as mixed content.
      const url = buildFunctionUrl('redeemCoupon', { forceProduction: true });
      const idToken = await currentUser.getIdToken();
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ couponCode: normalized }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        setError(data?.error || 'That code isn\'t valid. Check the email or contact your EM.');
        return;
      }
      const msg = data?.coupon?.message || 'Credits added to your account.';
      setRedeemedMessage(msg);
      setCode('');
    } catch (err) {
      console.error('SandboxRedemptionBanner activate error:', err);
      setError('Something went wrong. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  // Single-row compact layout: [input] [✓ activate] [× dismiss]
  // No card header, no description, no corner X. The input placeholder
  // ("Got a sandbox code?") carries the prompt copy; the two icon buttons
  // on the right give the user activate-or-dismiss in one motion. Border
  // and radius match the composer pill so the two surfaces read as a pair.
  const chatRadii = getChatRadii(theme);
  const chatBorders = getChatBorders(theme);
  const accentWarm = theme.palette.accentBubble?.warm || '#D4694E';

  const outerWrapperSx = {
    px: 1.25,
    pt: variant === 'composer' ? 1 : 1,
    pb: variant === 'composer' ? 0.75 : 1,
  };

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    width: '100%',
  };

  const inputSx = {
    flex: 1,
    minWidth: 0,
    height: 36,
    border: chatBorders.resting,
    borderRadius: chatRadii.pill,
    px: 1.75,
    fontSize: theme.typography.pxToRem(13),
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    color: 'text.primary',
    backgroundColor: 'transparent',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:focus': {
      borderColor: alpha(accentWarm, isDark ? 0.55 : 0.45),
      boxShadow: `0 0 0 2px ${alpha(accentWarm, isDark ? 0.14 : 0.10)}`,
    },
    '&::placeholder': {
      color: alpha(theme.palette.text.secondary, 0.55),
      fontWeight: theme.typography.fontWeightRegular,
    },
  };

  const iconButtonBaseSx = {
    width: 32,
    height: 32,
    borderRadius: chatRadii.circle,
    border: chatBorders.resting,
    flexShrink: 0,
    color: 'text.secondary',
    transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.action.hover, 0.12),
      color: 'text.primary',
    },
  };

  const activateIconSx = {
    ...iconButtonBaseSx,
    '&:not(.Mui-disabled)': {
      backgroundColor: theme.palette.text.primary,
      color: theme.palette.background.paper,
      borderColor: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: alpha(theme.palette.text.primary, 0.85),
        color: theme.palette.background.paper,
        borderColor: alpha(theme.palette.text.primary, 0.85),
      },
    },
    '&.Mui-disabled': {
      color: alpha(theme.palette.text.secondary, 0.45),
      borderColor: alpha(chatBorders.restingColor, 0.6),
    },
  };

  // ── Success state ── Same single-row footprint. Centered confirmation
  // message in place of the input, dismiss X on the right.
  if (redeemedMessage) {
    return (
      <Box sx={outerWrapperSx}>
        <Box sx={rowSx}>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: 36,
              border: `${chatBorders.width}px solid ${alpha(theme.palette.success.main, 0.4)}`,
              borderRadius: chatRadii.pill,
              px: 1.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(theme.palette.success.main, isDark ? 0.10 : 0.06),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: theme.typography.pxToRem(12),
                color: theme.palette.success.main,
                fontWeight: theme.typography.fontWeightRegular,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              ✓ {redeemedMessage}
            </Typography>
          </Box>
          <IconButton
            onClick={handleDismiss}
            size="small"
            aria-label="Dismiss"
            sx={iconButtonBaseSx}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // ── Activation prompt state ── Single row. Placeholder carries the
  // prompt copy; tick activates, X dismisses.
  return (
    <Box sx={outerWrapperSx}>
      <Box sx={rowSx}>
        <Box
          component="input"
          value={code}
          onChange={(e) => { setCode(e.target.value); if (error) setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleActivate(); }}
          placeholder="Got a sandbox code?"
          disabled={loading}
          aria-label="Sandbox code"
          sx={inputSx}
        />
        <IconButton
          onClick={handleActivate}
          disabled={loading || !code.trim()}
          size="small"
          aria-label="Activate"
          title="Activate"
          sx={activateIconSx}
        >
          {loading
            ? <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
            : <CheckIcon sx={{ fontSize: 16 }} />}
        </IconButton>
        <IconButton
          onClick={handleDismiss}
          size="small"
          aria-label="Dismiss"
          title="Dismiss"
          sx={iconButtonBaseSx}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {error && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'error.main',
            fontSize: theme.typography.pxToRem(11),
            mt: 0.625,
            lineHeight: 1.3,
            textAlign: 'center',
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

/**
 * OrgSeatBanner — the member's "claim your seat" prompt, shown IN the plugin so
 * a member never has to visit the console. When a signed-in user has no org
 * seat yet but their (SSO-verified) work email matches a pending invite or an
 * admin-enabled domain, this card offers one-click join. Reuses the exact
 * backend the console uses (listOrgJoinOptions -> acceptOrgInvite /
 * claimOrgSeatByDomain), so the security model is unchanged: SSO sign-in means
 * the email is provider-verified, and the click is the consent.
 *
 * Persistent (no dismiss): a seat waiting at their firm is the thing that makes
 * the product work, so we keep offering it until claimed.
 */
export const OrgSeatBanner = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isAuthenticated, currentUser } = useAuth();
  const orgAccess = useOrgAccess();
  const { isLoading: isPodLoading } = usePodMembership();

  const [match, setMatch] = useState(null); // { orgId, orgName, viaDomain }
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);

  // Discover a waiting seat once the user is known and confirmed seat-less.
  // listOrgJoinOptions matches on the AUTH TOKEN email (SSO-verified) and
  // returns nothing for users already in an org, so this is safe to call for
  // everyone who lands here.
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || !currentUser || orgAccess.isLoading || orgAccess.orgId) {
      setMatch(null);
      return undefined;
    }
    (async () => {
      try {
        // Force-refresh so a just-verified email (the verify link folded into the
        // welcome email) is reflected immediately — a cached token would still read
        // unverified and hide the seat the user can now actually claim.
        const idToken = await currentUser.getIdToken(true);
        const res = await fetch(buildFunctionUrl('listOrgJoinOptions', { forceProduction: true }), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({}),
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const invite = Array.isArray(data?.invites) && data.invites[0];
        if (invite) setMatch({ orgId: invite.orgId, orgName: invite.orgName, viaDomain: false });
        else if (data?.domainOrg) setMatch({ orgId: data.domainOrg.orgId, orgName: data.domainOrg.orgName, viaDomain: true });
        else setMatch(null);
      } catch (err) {
        // Silent: a discovery failure just means no card this render.
        console.warn('[OrgSeatBanner] join-option lookup failed:', err && err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, currentUser, orgAccess.isLoading, orgAccess.orgId]);

  if (!isAuthenticated || isPodLoading || orgAccess.orgId || !match) return null;

  const handleClaim = async () => {
    if (claiming || !currentUser) return;
    setClaiming(true);
    setError(null);
    try {
      const endpoint = match.viaDomain ? 'claimOrgSeatByDomain' : 'acceptOrgInvite';
      // Force-refresh: if the user just clicked the verify link, this picks up the
      // now-verified token so the claim passes instead of 403-ing on a stale token.
      const idToken = await currentUser.getIdToken(true);
      const res = await fetch(buildFunctionUrl(endpoint, { forceProduction: true }), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ orgId: match.orgId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403 && /verify/i.test(data?.error || '')) {
          setError('Verify your work email first, then try again.');
          return;
        }
        throw new Error(data?.error || 'Could not claim your seat. Try again.');
      }
      // Reload so the contexts refetch org/seat and the AI is immediately usable.
      if (typeof window !== 'undefined') window.location.reload();
    } catch (err) {
      console.error('[OrgSeatBanner] claim error:', err);
      setError(err.message || 'Could not claim your seat. Try again.');
    } finally {
      setClaiming(false);
    }
  };

  const accentWarm = theme.palette.accentBubble?.warm || '#D4694E';

  return (
    <Box sx={{ px: 1.25, pt: 1, pb: 0.75 }}>
      <Box
        sx={{
          border: `${getChatBorders(theme).width}px solid ${alpha(accentWarm, isDark ? 0.4 : 0.3)}`,
          borderRadius: getChatRadii(theme).card,
          backgroundColor: alpha(accentWarm, isDark ? 0.1 : 0.05),
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: theme.typography.pxToRem(13), fontWeight: 600, lineHeight: 1.3 }}>
            {match.viaDomain
              ? <><strong>{match.orgName}</strong> has Restack</>
              : <>{match.orgName} invited you to Restack</>}
          </Typography>
          <Typography sx={{ fontSize: theme.typography.pxToRem(12), color: 'text.secondary', lineHeight: 1.3, mt: 0.25 }}>
            {match.viaDomain
              ? 'Your work email is recognized — claim a seat to start.'
              : 'Claim your seat to start working.'}
          </Typography>
          {error && (
            <Typography sx={{ fontSize: theme.typography.pxToRem(11), color: 'error.main', mt: 0.5, lineHeight: 1.3 }}>
              {error}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClaim}
          disabled={claiming}
          aria-label="Claim your seat"
          title="Claim your seat"
          sx={{
            flexShrink: 0,
            px: 1.75,
            height: 34,
            borderRadius: getChatRadii(theme).pill,
            width: 'auto',
            backgroundColor: theme.palette.text.primary,
            color: theme.palette.background.paper,
            fontSize: theme.typography.pxToRem(13),
            fontWeight: 600,
            '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.85) },
            '&.Mui-disabled': { backgroundColor: alpha(theme.palette.text.primary, 0.4), color: theme.palette.background.paper },
          }}
        >
          {claiming ? <CircularProgress size={15} thickness={5} sx={{ color: 'inherit' }} /> : 'Claim seat'}
        </IconButton>
      </Box>
    </Box>
  );
};

const ChatHeaderBar = ({
  onNewChat,
  isCreatingNewChat = false,
  canCreateNewChat = true,
  onOpenHistorySidebar,
  chatTitle,
  // Cross-app connector state. It lives here, at the right edge of the
  // header, rather than on the composer's bottom row: it describes the pane,
  // not the message being written.
  app = 'powerpoint',
  conductorOnline = false,
  conductorPeers = [],
  onConnectorClick = null,
}) => {
  const theme = useTheme();
  const hoverBg = alpha(theme.palette.action.hover, 0.08);

  return (
    <ChatHeader>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.25), minWidth: 0, flex: 1 }}>
        {/* Hamburger menu */}
        <IconButton
          onClick={onOpenHistorySidebar}
          size="small"
          aria-label="Open chat history and workflows"
          title="Chat history and workflows"
          sx={{
            color: 'text.secondary',
            flexShrink: 0,
            '&:hover': { backgroundColor: hoverBg }
          }}
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* New chat */}
        <IconButton
          onClick={onNewChat}
          disabled={isCreatingNewChat || !canCreateNewChat}
          size="small"
          aria-label={isCreatingNewChat ? 'Creating new chat' : 'Start a new chat'}
          title={!canCreateNewChat ? "Send a message first to enable new chat" : (isCreatingNewChat ? "Creating..." : "New chat")}
          sx={{
            color: (isCreatingNewChat || !canCreateNewChat) ? 'text.disabled' : 'text.secondary',
            opacity: !canCreateNewChat ? 0.5 : 1,
            flexShrink: 0,
            '&:hover': {
              backgroundColor: (isCreatingNewChat || !canCreateNewChat) ? 'transparent' : hoverBg
            }
          }}
        >
          {isCreatingNewChat ? <CircularProgress size={16} thickness={4} /> : <AddIcon sx={{ fontSize: 20 }} />}
        </IconButton>

        {/* Chat title */}
        {chatTitle && (
          <Typography
            variant="body2"
            title={chatTitle}
            sx={{
              ml: theme.spacing(0.75),
              fontSize: theme.typography.pxToRem(12),
              fontWeight: 500,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            {chatTitle}
          </Typography>
        )}
      </Box>

      <ConnectorStatusIndicator
        app={app}
        conductorOnline={conductorOnline}
        conductorPeers={conductorPeers}
        onConnectorClick={onConnectorClick}
      />
    </ChatHeader>
  );
};

// =============================================================================
// SECTION 5b: HISTORY SIDEBAR PANEL
// =============================================================================

// =============================================================================
// FeedbackButton — icon button + popup that emails feedback to the founder.
// =============================================================================
// Lives in the history sidebar account row, next to Settings / Sign out. Posts
// to the same `sendFeedback` Cloud Function the demo form uses, which emails
// FEEDBACK_RECIPIENT (harsh@re-stack.com) via Resend with the user's email as
// reply-to, so a reply goes straight back to them. Self-contained: owns its own
// dialog + submit state so the parent stays clean.
const FeedbackButton = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');

  const close = () => {
    if (status === 'sending') return;
    setOpen(false);
    // Reset after the close animation so the form is fresh next open.
    setTimeout(() => { setText(''); setStatus('idle'); setErrorMsg(''); }, 220);
  };

  const submit = async () => {
    const message = text.trim();
    if (!message) {
      setErrorMsg('Please write a little something first.');
      setStatus('error');
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(buildFunctionUrl('sendFeedback', { forceProduction: true }), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedbackType: 'Plugin feedback',
          userName: currentUser?.displayName || currentUser?.email || 'Plugin user',
          userEmail: currentUser?.email || '',
          userId: currentUser?.uid || '',
          feedback: message,
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setStatus('sent');
    } catch (e) {
      setErrorMsg('Could not send right now. Please email hi@re-stack.com.');
      setStatus('error');
    }
  };

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        size="small"
        title="Send feedback"
        sx={{
          color: 'text.secondary',
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }}
      >
        <FeedbackIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Dialog
        open={open}
        onClose={close}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: getChatRadii(theme).card,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            border: getChatBorders(theme).resting,
            m: 2,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: theme.typography.pxToRem(15), fontWeight: 600, color: 'text.primary' }}>
              Send feedback
            </Typography>
            <IconButton onClick={close} size="small" sx={{ color: 'text.secondary', mr: -0.5 }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {status === 'sent' ? (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: getChatRadii(theme).nested,
                border: `${getChatBorders(theme).width}px solid ${alpha(theme.palette.success.main, 0.35)}`,
                backgroundColor: alpha(theme.palette.success.main, 0.08),
              }}
            >
              <Typography sx={{ fontSize: theme.typography.pxToRem(13), fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
                Thanks — got it.
              </Typography>
              <Typography sx={{ fontSize: theme.typography.pxToRem(12), color: 'text.secondary', lineHeight: 1.45 }}>
                Your note goes straight to the founder{currentUser?.email ? `, and we'll reply to ${currentUser.email}` : ''}.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.25 }}>
                <Button
                  onClick={close}
                  size="small"
                  sx={{ textTransform: 'none', fontSize: theme.typography.pxToRem(13), fontWeight: 600, borderRadius: getChatRadii(theme).pill }}
                >
                  Done
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: theme.typography.pxToRem(12.5), color: 'text.secondary', lineHeight: 1.5, mb: 1.25 }}>
                Tell us what's working or what's broken. It goes straight to the founder.
              </Typography>
              <TextField
                autoFocus
                multiline
                minRows={4}
                maxRows={8}
                fullWidth
                value={text}
                onChange={(e) => { setText(e.target.value); if (status === 'error') setStatus('idle'); }}
                placeholder="What's on your mind?"
                disabled={status === 'sending'}
                InputProps={{
                  sx: {
                    fontSize: theme.typography.pxToRem(13),
                    borderRadius: getChatRadii(theme).nested,
                    alignItems: 'flex-start',
                  },
                }}
              />
              {status === 'error' && (
                <Typography sx={{ mt: 0.75, fontSize: theme.typography.pxToRem(12), color: 'error.main', lineHeight: 1.4 }}>
                  {errorMsg}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.25 }}>
                <Button
                  onClick={close}
                  disabled={status === 'sending'}
                  size="small"
                  sx={{ textTransform: 'none', fontSize: theme.typography.pxToRem(13), color: 'text.secondary', borderRadius: getChatRadii(theme).pill }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={status === 'sending' || !text.trim()}
                  variant="contained"
                  disableElevation
                  size="small"
                  sx={{ textTransform: 'none', fontSize: theme.typography.pxToRem(13), fontWeight: 600, borderRadius: getChatRadii(theme).pill, px: 2 }}
                >
                  {status === 'sending' ? 'Sending…' : 'Send'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Dialog>
    </>
  );
};

const HistorySidebarPanel = ({ open, onClose, chats = [], currentChatId, switchToChat, onNewChat, onSuggestionClick, isCreatingNewChat = false, canCreateNewChat = true, onTogglePin = null, onArchiveChat = null, app = 'powerpoint' }) => {
  const theme = useTheme();
  const { signOut, currentUser } = useAuth();
  const { isDarkMode, toggleDarkMode } = useUser();
  // Drawer is segmented: History | Workflows. (The old Firm style tab was
  // removed: the template IS the firm's look, managed under Workflows >
  // Templates, and the firm guide itself is a console/admin surface.)
  const [activeTab, setActiveTab] = useState('history');
  const [historyMode, setHistoryMode] = useState('active');
  // The workflow editor needs room: while it is open the drawer expands
  // (ChatHistorySidebar's `wide` prop).
  const [workflowEditorOpen, setWorkflowEditorOpen] = useState(false);
  const SIDEBAR_TABS = [
    { key: 'history', label: 'Chats' },
    { key: 'workflows', label: 'Workflows' },
  ];
  const userChats = (chats || []).filter(
    (chat) => !(
      chat.conductorMinted
      && (chat.messageCount || 0) < 1
      && !(chat.messages || []).some((message) => message.sender === 'user')
    )
  );
  const archivedCount = userChats.filter((chat) => chat.archived === true).length;

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Unmount the drawer while closed so its controls cannot remain in the Tab
  // order behind an aria-hidden panel.
  if (!open) return null;

  return (
    <>
      <ChatHistorySidebarBackdrop open={open} onClick={onClose} />
      <ChatHistorySidebar
        open={open}
        wide={activeTab === 'workflows' && workflowEditorOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Chat history and workflows"
      >
        {/* Tab strip: History | Workflows | Firm style */}
        <Box sx={{
          display: 'flex', gap: 0.5, px: 1, pt: 1, pb: 0.75, flexShrink: 0,
          borderBottom: `${getChatBorders(theme).width}px solid ${theme.palette.divider}`,
        }} role="tablist" aria-label="Sidebar sections">
          {SIDEBAR_TABS.map((t) => (
            <Box
              key={t.key}
              component="button"
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              tabIndex={activeTab === t.key ? 0 : -1}
              autoFocus={activeTab === t.key}
              onClick={() => setActiveTab(t.key)}
              onKeyDown={(event) => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const tabs = Array.from(event.currentTarget.parentElement?.querySelectorAll('[role="tab"]') || []);
                if (tabs.length === 0) return;
                const currentIndex = tabs.indexOf(event.currentTarget);
                const nextIndex = event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? tabs.length - 1
                    : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
                const nextKey = SIDEBAR_TABS[nextIndex]?.key;
                if (nextKey) setActiveTab(nextKey);
                tabs[nextIndex]?.focus();
              }}
              sx={{
                flex: 1, appearance: 'none', cursor: 'pointer', border: 'none',
                borderRadius: getChatRadii(theme).nested, py: 0.6,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.pxToRem(12),
                fontWeight: activeTab === t.key ? 600 : 500,
                color: activeTab === t.key ? theme.palette.primary.main : theme.palette.text.secondary,
                backgroundColor: activeTab === t.key ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              }}
            >
              {t.label}
            </Box>
          ))}
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close sidebar"
            title="Close sidebar"
            sx={{ width: 28, height: 28, flexShrink: 0, color: 'text.secondary' }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {activeTab === 'history' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.75, borderBottom: `${getChatBorders(theme).width}px solid ${theme.palette.divider}` }}>
            {[
              { key: 'active', label: 'Active' },
              { key: 'archived', label: archivedCount > 0 ? `Archived ${archivedCount}` : 'Archived' },
            ].map((item) => (
              <Box
                key={item.key}
                component="button"
                type="button"
                onClick={() => setHistoryMode(item.key)}
                sx={{
                  appearance: 'none', border: 'none', cursor: 'pointer', px: 0.9, py: 0.35,
                  borderRadius: getChatRadii(theme).chip, fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.pxToRem(11), fontWeight: historyMode === item.key ? 600 : 500,
                  color: historyMode === item.key ? 'text.primary' : 'text.secondary',
                  backgroundColor: historyMode === item.key ? theme.palette.action.selected : 'transparent',
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>
        )}

        {/* Body swaps with the active tab */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {activeTab === 'workflows' && (
            <WorkflowsPanel
              app={app}
              onSuggestionClick={onSuggestionClick}
              onClose={onClose}
              onEditorOpenChange={setWorkflowEditorOpen}
            />
          )}
          {activeTab === 'history' && (
          <Box sx={{ px: 0.75, py: 1 }}>
          {(() => {
            // Connector-minted chats the USER never typed in are plumbing,
            // not history: hide them (an injected cross-app turn adds AI
            // messages, so an emptiness check is not enough). A connector
            // chat the user actually joined stays visible.
            const visibleChats = userChats.filter((chat) => (
              historyMode === 'archived' ? chat.archived === true : chat.archived !== true
            ));
            if (visibleChats.length === 0) {
              return (
                <Box sx={{ padding: theme.spacing(4, 2), textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2" sx={{ fontSize: theme.typography.pxToRem(13), mb: 0.5 }}>
                    {historyMode === 'archived' ? 'No archived chats' : 'No chats yet'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: theme.typography.pxToRem(12), color: 'text.disabled' }}>
                    {historyMode === 'archived' ? 'Archive a chat to move it here' : 'Start a new conversation'}
                  </Typography>
                </Box>
              );
            }

            // Sectioned list: Pinned first, then Recents (mirrors the
            // Claude Code sidebar). The list arrives pinned-first already;
            // grouping adds the section headers and keeps them stable.
            const pinnedChats = visibleChats.filter((c) => !!c.pinned);
            const recentChats = visibleChats.filter((c) => !c.pinned);

            const sectionHeader = (label) => (
              <Typography
                key={`section-${label}`}
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1.25,
                  pt: 1,
                  pb: 0.4,
                  fontSize: theme.typography.pxToRem(10.5),
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  color: 'text.disabled',
                }}
              >
                {label}
              </Typography>
            );

            const renderChatRow = (chat) => {
              const isActive = chat.id === currentChatId;
              const firstUserMessage = (chat.messages || []).find(m => m.sender === 'user');
              const hasGeneratedTitle = chat.name && chat.name !== 'New chat';
              const chatTitle = hasGeneratedTitle ? chat.name : (firstUserMessage?.content || chat.name || 'Chat');
              const timestamp = chat.updatedAt || chat.lastModified || chat.createdAt;
              const isPinned = !!chat.pinned;

              return (
                <ChatHistoryItem
                  key={chat.id}
                  onClick={() => {
                    if (switchToChat && !isActive) {
                      switchToChat(chat.id);
                    }
                    onClose();
                  }}
                  sx={{
                    position: 'relative',
                    backgroundColor: isActive ? theme.palette.action.selected : 'transparent',
                    cursor: isActive ? 'default' : 'pointer',
                    '&:hover': {
                      backgroundColor: isActive
                        ? theme.palette.action.selected
                        : theme.palette.action.hover
                    },
                    '&:hover .chat-action': { opacity: 1 },
                  }}
                >
                  {/* Single row: current-chat dot, title, actions, and date.
                      The dot marks the live chat the same
                      way the Claude Code sidebar marks the active session. */}
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    {isActive && (
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: getChatRadii(theme).circle,
                          backgroundColor: alpha(theme.palette.text.secondary, 0.6),
                          flexShrink: 0,
                          mr: 0.15,
                        }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: isActive ? 600 : 500,
                        fontSize: theme.typography.pxToRem(13),
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {chatTitle}
                    </Typography>
                    {onTogglePin && (
                      <IconButton
                        className="chat-action"
                        size="small"
                        title={isPinned ? 'Unpin chat' : 'Pin chat'}
                        onClick={(e) => {
                          e.stopPropagation();
                          onTogglePin(chat.id, !isPinned);
                        }}
                        sx={{
                          width: 28, height: 28, p: 0, flexShrink: 0,
                          opacity: isPinned ? 1 : 0.58,
                          transition: 'opacity 0.15s ease',
                          color: isPinned ? 'text.secondary' : 'text.disabled',
                          '&:hover': { color: 'text.secondary', backgroundColor: alpha(theme.palette.text.primary, 0.05) },
                        }}
                      >
                        {isPinned
                          ? <PushPinIcon sx={{ fontSize: 15 }} />
                          : <PushPinOutlinedIcon sx={{ fontSize: 15 }} />}
                      </IconButton>
                    )}
                    {onArchiveChat && (
                      <IconButton
                        className="chat-action"
                        size="small"
                        title={chat.archived ? 'Restore chat' : 'Archive chat'}
                        onClick={(event) => {
                          event.stopPropagation();
                          onArchiveChat(chat.id, !chat.archived);
                        }}
                        sx={{
                          width: 28, height: 28, p: 0, flexShrink: 0, opacity: chat.archived ? 1 : 0.58,
                          transition: 'opacity 0.15s ease', color: 'text.disabled',
                          '&:hover': { color: 'text.secondary', backgroundColor: alpha(theme.palette.text.primary, 0.05) },
                        }}
                      >
                        <ArchiveIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    )}
                    {timestamp && (
                      <Typography
                        variant="caption"
                        sx={{ fontSize: theme.typography.pxToRem(10), color: 'text.disabled', whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        {formatChatTimestampShort(timestamp)}
                      </Typography>
                    )}
                  </Box>
                </ChatHistoryItem>
              );
            };

            return (
              <>
                {pinnedChats.length > 0 && sectionHeader('Pinned')}
                {pinnedChats.map(renderChatRow)}
                {pinnedChats.length > 0 && recentChats.length > 0 && sectionHeader('Recents')}
                {recentChats.map(renderChatRow)}
              </>
            );
          })()}
          </Box>
          )}
        </Box>

        {/* Footer stack: credits meter, sandbox banner (free / trial users
            only), then the account row. Org members see THEIR OWN seat usage
            (MemberCreditsBar) plus a pulling-from-firm state once the seat
            allowance is spent; the firm pool itself stays numberless here
            (figures live in the admin console). Non-org users get the pod
            balance bar (CreditBalanceBar), which self-hides at zero. */}
        <Box sx={{
          borderTop: `${getChatBorders(theme).width}px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}>
          <MemberCreditsBar />
          <CreditBalanceBar />
          <SandboxRedemptionBanner variant="sidebar" />

          {/* Account row — email + theme toggle + settings link + sign out.
              Previously the whole email row was a single click target that
              opened re-stack.com/settings in a new tab. That hijacked the
              email display surface and gave no visual affordance for the
              click. Replaced with: email is plain text, a dedicated
              Settings icon button opens the settings page, theme + signout
              keep their existing icon buttons. */}
          <Box sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            borderTop: `${getChatBorders(theme).width}px solid ${theme.palette.divider}`,
          }} role="group" aria-label="Account controls">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.2,
                  fontSize: theme.typography.pxToRem(9.5),
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  color: 'text.disabled',
                  lineHeight: 1.2,
                }}
              >
                Account
              </Typography>
              <Typography
                variant="caption"
                title={currentUser?.email || ''}
                sx={{
                  display: 'block',
                  fontSize: theme.typography.pxToRem(11),
                  fontWeight: 500,
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                }}
              >
                {currentUser?.email || ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.125, flexShrink: 0 }}>
              <FeedbackButton />
              {/* Settings icon removed: the console is admin-only now, so the
                  working pane carries no settings entry point. */}
              <IconButton
                onClick={toggleDarkMode}
                size="small"
                title={isDarkMode ? "Light mode" : "Dark mode"}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
              >
                {isDarkMode ? <LightModeIcon sx={{ fontSize: 16 }} /> : <DarkModeIcon sx={{ fontSize: 16 }} />}
              </IconButton>
              <IconButton
                onClick={() => {
                  // Mark this as an explicit sign-out so the plugin does NOT
                  // immediately re-sign-in via silent SSO — the user may want
                  // a different account. Session-scoped: auto-SSO re-enables
                  // next time the task pane is opened.
                  try { sessionStorage.setItem('restack_sso_suppressed', '1'); } catch (e) { /* ignore */ }
                  signOut();
                }}
                size="small"
                title="Sign out"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
              >
                <LogoutIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </ChatHistorySidebar>
    </>
  );
};

// =============================================================================
// SECTION 6: LOOP TIMELINE
// Original File: LoopTimelineInline.js
// =============================================================================

// Step status uses the same semantic pairing as ResearchStatusDot (complete ->
// success, running -> info), so a timeline and a status dot describing the same
// state agree. This replaced a hardcoded iOS blue that belonged to no palette
// and washed the active step cool inside an otherwise warm, neutral taskpane.
const statusColorMap = (theme) => ({
  completed: theme.palette.success.main,
  in_progress: theme.palette.info.main,
  error: theme.palette.error.main,
  abandoned: theme.palette.warning.main,
  default: theme.palette.info.main
});

const statusLabelFallback = {
  in_progress: 'In progress',
  completed: 'Completed',
  error: 'Error',
  abandoned: 'Abandoned'
};

const formatStepTitle = (step, index) => {
  const priorityLabels = [
    step?.intentLabel,
    step?.statusLabel,
    step?.primaryTool,
    Array.isArray(step?.tools) ? step.tools[step.tools.length - 1] : null,
    step?.tag,
    step?.summary
  ].filter((label) => typeof label === 'string' && label.trim().length > 0);

  if (priorityLabels.length > 0) {
    return priorityLabels[0].trim();
  }

  if (typeof step?.stepNumber === 'number') {
    return `Action ${step.stepNumber}`;
  }

  return `Action ${index + 1}`;
};

const LoopTimelineInline = ({ data }) => {
  const theme = useTheme();
  const tokens = theme.restack?.tokens ?? {};
  const steps = Array.isArray(data?.steps) ? data.steps : [];
  if (steps.length === 0) {
    return null;
  }

  const colors = statusColorMap(theme);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(tokens.spacing.nano + tokens.spacing.quark ?? 0.75),
        marginBottom: theme.spacing(tokens.spacing.sm ?? 2)
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(tokens.spacing.sm ?? 2)
        }}
      >
        {steps.map((step, index) => {
          const status = step?.status || 'in_progress';
          const statusColor = colors[status] || colors.default;
          const isActive = status === 'in_progress';
          const title = formatStepTitle(step, index);
          const statusLabel = (step?.statusLabel || statusLabelFallback[status] || '').trim();
          const toolLabel = (step?.primaryTool || (step?.tools && step.tools[step.tools.length - 1]) || '').trim();
          const detailParts = [];
          if (statusLabel && statusLabel.toLowerCase() !== title.toLowerCase()) {
            detailParts.push(statusLabel);
          }
          if (toolLabel && !detailParts.some(part => part.toLowerCase() === toolLabel.toLowerCase())) {
            detailParts.push(toolLabel);
          }
          const labelColor = isActive
            ? alpha(theme.palette.text.primary, 0.92)
            : alpha(theme.palette.text.primary, 0.74);
          const detailColor = isActive
            ? alpha(theme.palette.text.secondary, 0.9)
            : alpha(theme.palette.text.secondary, 0.75);

          return (
            <Box
              key={`loop-step-${step?.stepNumber ?? index}`}
              sx={{
                position: 'relative',
                padding: theme.spacing(tokens.spacing?.nano ?? 0.75, tokens.spacing?.sm ?? 1.5),
                borderRadius: getChatRadii(theme).nested,
                backgroundColor: isActive
                  ? alpha(statusColor, theme.palette.mode === 'dark' ? 0.16 : 0.1)
                  : 'transparent'
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.fontWeightMedium,
                  color: labelColor,
                  lineHeight: 1.4,
                  position: 'relative'
                }}
              >
                {title}
              </Typography>
              {detailParts.length > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    marginTop: theme.spacing(tokens.spacing.quark ?? 0.25),
                    color: detailColor,
                    fontWeight: theme.typography.fontWeightRegular,
                    lineHeight: 1.4,
                    position: 'relative'
                  }}
                >
                  {detailParts.join(' • ')}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// =============================================================================
// SECTION 7: RATE LIMIT CARD
// Original File: RateLimitCard.js
// =============================================================================

const RateLimitCard = ({ adminFirstName = null }) => {
  const theme = useTheme();
  const { tokens } = theme.restack;

  // Adoption guard: the member working UI never shows a balance, a deficit, or a
  // running countdown — and no action button (a member can't manage billing; the
  // old "Open billing" link dead-ended at the /admin access gate). Just the soft
  // "ask your admin to top up" message, personalized with the admin's first
  // name when the backend knows it (a name converts better than a role, and a
  // name is metadata — never a number). Billing Admins top up from the console.
  return (
    <UnifiedCardContainer
      sx={{
        backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
        border: `${getChatBorders(theme).width}px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.3 : 0.2)}`,
        marginBottom: theme.spacing(tokens.spacing.xs)
      }}
    >
      <UnifiedCardHeader>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1.5),
          flex: 1,
          minWidth: 0
        }}>
          <Box sx={{
            color: theme.palette.warning.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: theme.spacing(4),
            height: theme.spacing(4),
            borderRadius: getChatRadii(theme).circle,
            backgroundColor: alpha(theme.palette.warning.main, 0.1),
            flexShrink: 0
          }}>
            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <UnifiedCardTitle sx={{ color: theme.palette.warning.main, mb: 0.25 }}>
              Shared credits used up
            </UnifiedCardTitle>
          </Box>
        </Box>
      </UnifiedCardHeader>

      <UnifiedCardContent sx={{ paddingTop: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            ...tokens.typography.bodySm
          }}
        >
          Your team's shared AI credits are used up for now. Ask {adminFirstName || 'your workspace admin'} to top up and you'll be back in action.
        </Typography>
      </UnifiedCardContent>
    </UnifiedCardContainer>
  );
};

// A refused turn needs a card, not a status label. The backend explains the
// refusal through sendStatus and then immediately sends turn_complete, and a
// status label is transient: the completion wipes it before it can be read. So
// a turn the server declined used to look exactly like a turn that produced
// nothing, which is the one reading that tells the user to retry forever.
const TurnErrorCard = ({ message = null }) => {
  const theme = useTheme();
  const { tokens } = theme.restack;

  return (
    <UnifiedCardContainer
      sx={{
        backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
        border: `${getChatBorders(theme).width}px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.3 : 0.2)}`,
        marginBottom: theme.spacing(tokens.spacing.xs)
      }}
    >
      <UnifiedCardHeader>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1.5),
          flex: 1,
          minWidth: 0
        }}>
          <Box sx={{
            color: theme.palette.warning.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: theme.spacing(4),
            height: theme.spacing(4),
            borderRadius: getChatRadii(theme).circle,
            backgroundColor: alpha(theme.palette.warning.main, 0.1),
            flexShrink: 0
          }}>
            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <UnifiedCardTitle sx={{ color: theme.palette.warning.main, mb: 0.25 }}>
              This turn could not run
            </UnifiedCardTitle>
          </Box>
        </Box>
      </UnifiedCardHeader>

      <UnifiedCardContent sx={{ paddingTop: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            ...tokens.typography.bodySm
          }}
        >
          {message || 'The request was stopped before it started. Nothing in your document was changed.'}
        </Typography>
      </UnifiedCardContent>
    </UnifiedCardContainer>
  );
};

// =============================================================================
// SECTION 8: SLIDE ACTIVITY CARD
// Original File: cards/SlideActivityCard.js
// =============================================================================

const SlideActivityCard = ({
  variant,
  data = {},
  currentSlideCount = 0,
  isMessageStreaming = false,
  ...rest
}) => {
  const operation = variant === 'creation' ? 'create' : 'edit';

  // FIX: Only show isLoading=true if the message is actually streaming
  // This prevents stale "Preparing..." state from persisted messages
  const effectiveIsLoading = isMessageStreaming && data.isLoading;

  return (
    <SlideBuilderCard
      operation={operation}
      status={data.status}
      count={data.count}
      isLoading={effectiveIsLoading}
      duration={data.duration}
      slideIds={data.slideIds || []}
      slideIndices={data.slideIndices || []}
      completedCount={data.completedCount || data.completedSlides || 0}
      progressSlides={data.progressSlides || []}
      currentSlideCount={currentSlideCount}
      editedSlides={data.editedSlides || []}
      slideNumbers={data.slideNumbers || []}
      {...rest}
    />
  );
};

// =============================================================================
// SECTION 9: SHARED STYLES
// Original File: cards/sharedCardStyles.js
// =============================================================================

export const buildCardListSx = (theme, tokens) => ({
  margin: 0,
  paddingLeft: theme.spacing(tokens.spacing.sm + tokens.spacing.nano),
  listStyle: 'disc',
  listStylePosition: 'outside',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(tokens.spacing.nano + tokens.spacing.quark),
  color: alpha(theme.palette.text.primary, 0.85)
});

export const buildListItemTextSx = (theme, tokens) => ({
  color: alpha(theme.palette.text.primary, 0.85),
  lineHeight: 1.5
});

export const buildCardCaptionSx = (theme) => ({
  color: alpha(theme.palette.text.secondary, 0.85)
});

// =============================================================================
// MODULE EXPORTS
// =============================================================================

// Note: Most components are exported inline above
// Only components without inline exports are listed here
export {
  WelcomeCards,
  ChatHeaderBar,
  HistorySidebarPanel,
  LoopTimelineInline,
  RateLimitCard,
  SlideActivityCard
};
